import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const client = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Token injection ──────────────────────────────────────────
// Reads directly from localStorage so it works immediately on load
// without needing setAuthTokenGetter to be called first.
client.interceptors.request.use(async (config) => {
  try {
    const token = localStorage.getItem('mor_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // No token available
  }
  return config;
});

// ── Response error normalisation ─────────────────────────────
client.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const message =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      'An unexpected error occurred.';

    const status = err.response?.status;

    if (status === 401) {
      localStorage.removeItem('mor_token');
      localStorage.removeItem('mor_user');
      window.location.href = '/login';
    }

    const error   = new Error(message);
    error.status  = status;
    error.data    = err.response?.data;
    return Promise.reject(error);
  }
);

// ── Typed helpers ─────────────────────────────────────────────
export const api = {
  get:    (url, config)       => client.get(url, config),
  post:   (url, data, config) => client.post(url, data, config),
  put:    (url, data, config) => client.put(url, data, config),
  patch:  (url, data, config) => client.patch(url, data, config),
  delete: (url, config)       => client.delete(url, config),

  // Server-rendered file downloads (e.g. the Expense Analysis export). Used
  // instead of building the workbook client-side because a filtered expense
  // selection can run to tens of thousands of rows — far more than the UI ever
  // loads into memory — and the server already owns the filter definition, so
  // the export cannot drift from what the totals say.
  getBlob: (url, config) =>
    client.get(url, { ...config, responseType: 'blob', timeout: 120000 }),

  // The confirm half of a preview -> commit upload is where the batch is
  // actually written, and for a large file it is the SLOWER half: the expense
  // feed's re-baseline is 13,393 inserts and 11,782 supersedes in one
  // transaction. The default 30s does not cover that, and it timed out on the
  // real file at exactly 30s.
  //
  // The failure mode is worse than a slow request. Express keeps working after
  // the browser gives up, and commitBatch is transactional, so the commit
  // SUCCEEDS while the operator is told it failed -- and their next instinct
  // is to run it again. (A second attempt is refused, because the batch is no
  // longer in `preview`, but "Batch is already committed" is a confusing way
  // to learn that the first one worked.)
  //
  // Same allowance as upload(), for the same reason.
  commit: (url, data) => client.post(url, data, { timeout: 300000 }),

  upload: (url, formData, onProgress) =>
    client.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Large files (tens of thousands of rows) can take a while to parse +
      // ingest server-side; override the default 30s client timeout.
      timeout: 300000,
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    }),
};

export default client;